import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const PermissionsContext = createContext({
    effective: new Set(),
    grants: [],
    ready: false,
    refresh: async () => {},
});

export function PermissionsProvider({ children }) {
    const { user } = useAuth();
    const [effective, setEffective] = useState(new Set());
    const [grants, setGrants] = useState([]);
    const [ready, setReady] = useState(false);

    const refresh = useCallback(async () => {
        if (!user || user === false) {
            setEffective(new Set());
            setGrants([]);
            setReady(true);
            return;
        }
        try {
            const { data } = await api.get("/permissions/me");
            setEffective(new Set(data.effective || []));
            setGrants(data.grants || []);
        } catch (_e) {
            setEffective(new Set());
            setGrants([]);
        } finally {
            setReady(true);
        }
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    const value = useMemo(() => ({ effective, grants, ready, refresh }), [effective, grants, ready, refresh]);

    return (
        <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
    );
}

/** Returns true iff the current user has `key` permission. Considers role
 * defaults + active global overrides (the server already merged these). For
 * module-scoped grants, pass the moduleId — we'll check the grants list
 * directly. */
export function useHasPermission(key, moduleId = null) {
    const { effective, grants } = useContext(PermissionsContext);
    return useMemo(() => {
        if (!key) return false;
        // Global allow/role default
        if (effective.has(key)) {
            // Honor a module-scoped DENY override if present
            const moduleDeny = grants.find((g) =>
                g.permission_key === key && g.effect === "deny" && g.scope === "module" && g.module_id === moduleId,
            );
            if (moduleDeny) return false;
            return true;
        }
        // Module-scoped ALLOW
        if (moduleId) {
            const moduleAllow = grants.find((g) =>
                g.permission_key === key && g.effect === "allow" && g.scope === "module" && g.module_id === moduleId,
            );
            if (moduleAllow) return true;
        }
        return false;
    }, [effective, grants, key, moduleId]);
}

export function usePermissions() {
    return useContext(PermissionsContext);
}
